import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { mapApiError } from "../../../../shared/utils/mapApiError";
import {
  createContenido,
  getContenido,
  listContenidoAreas,
  listContenidoBranches,
  listContenidoRoles,
  updateContenido,
} from "../api/contenidoApi";
import {
  CATEGORY_OPTIONS,
  STATUS_OPTIONS,
  STATUS_LABELS,
} from "../constants/contentOptions";
import RichTextEditor from "../components/RichTextEditor";

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function toDateInputValue(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function buildReadUntilIso(dateValue) {
  if (!dateValue) return null;

  const [year, month, day] = String(dateValue).split("-").map(Number);
  const date = new Date(year, month - 1, day, 23, 59, 59, 999);

  if (Number.isNaN(date.getTime())) return null;

  return date.toISOString();
}

function buildFormState(content) {
  return {
    title: content?.title ?? "",
    category: content?.category ?? "POLITICA",
    status: content?.status ?? "DRAFT",
    body: content?.body ?? "",

    readRequirement: content?.readRequirement ?? "NONE",
    readRepeatEvery: content?.readRepeatEvery ? String(content.readRepeatEvery) : "1",
    readWeekday: content?.readWeekday ? String(content.readWeekday) : "1",
    readMonthDay: content?.readMonthDay ? String(content.readMonthDay) : "1",
    readUntil: toDateInputValue(content?.readUntil),
  };
}

function buildScopeState(content) {
  const branchIds = Array.isArray(content?.branchIds) ? content.branchIds.map(String) : [];
  const areaIds = Array.isArray(content?.areaIds) ? content.areaIds.map(String) : [];
  const roleIds = Array.isArray(content?.roleIds) ? content.roleIds.map(String) : [];
  const hasAnyScopedAxis = branchIds.length > 0 || areaIds.length > 0 || roleIds.length > 0;

  return {
    all: !hasAnyScopedAxis,
    branch: branchIds.length > 0,
    area: areaIds.length > 0,
    role: roleIds.length > 0,
    branchIds,
    areaIds,
    roleIds,
  };
}

function buildSnapshot({ form, scopeAll, scopeBranch, scopeArea, scopeRole, branchIds, areaIds, roleIds }) {
  return {
    form,
    scopeAll,
    scopeBranch,
    scopeArea,
    scopeRole,
    branchIds: [...branchIds].sort(),
    areaIds: [...areaIds].sort(),
    roleIds: [...roleIds].sort(),
  };
}

function readCurrentUserDisplayName() {
  try {
    const raw = localStorage.getItem("sabi_user");
    if (!raw) return "";

    const user = JSON.parse(raw);
    return user?.displayName || user?.display_name || user?.name || "";
  } catch {
    return "";
  }
}

function renderSelectedNames(selected, options, emptyLabel) {
  if (!Array.isArray(selected) || selected.length === 0) return emptyLabel;

  const names = selected
    .map((id) => options.find((item) => String(item.id) === String(id))?.name)
    .filter(Boolean);

  return names.length ? names.join(", ") : emptyLabel;
}

const READ_REQUIREMENT_OPTIONS = [
  { value: "NONE", label: "No obligatoria" },
  { value: "ONCE", label: "Una vez" },
  { value: "WEEKLY", label: "Semanal" },
  { value: "MONTHLY", label: "Mensual" },
];

const WEEKDAY_OPTIONS = [
  { value: "1", label: "Lunes" },
  { value: "2", label: "Martes" },
  { value: "3", label: "Miércoles" },
  { value: "4", label: "Jueves" },
  { value: "5", label: "Viernes" },
  { value: "6", label: "Sábado" },
  { value: "7", label: "Domingo" },
];

export default function ContenidoFormPage() {
  const navigate = useNavigate();
  const { contentId } = useParams();
  const isEditing = Boolean(contentId);

  const [content, setContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(isEditing);
  const [loadingReferences, setLoadingReferences] = useState(true);
  const [form, setForm] = useState(() => buildFormState(null));
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roles, setRoles] = useState([]);
  const [scopeAll, setScopeAll] = useState(true);
  const [scopeBranch, setScopeBranch] = useState(false);
  const [scopeArea, setScopeArea] = useState(false);
  const [scopeRole, setScopeRole] = useState(false);
  const [selectedBranchIds, setSelectedBranchIds] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [initialSnapshot, setInitialSnapshot] = useState(() =>
    buildSnapshot({
      form: buildFormState(null),
      scopeAll: true,
      scopeBranch: false,
      scopeArea: false,
      scopeRole: false,
      branchIds: [],
      areaIds: [],
      roleIds: [],
    })
  );

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [branchesData, areasData, rolesData] = await Promise.all([
          listContenidoBranches(),
          listContenidoAreas(),
          listContenidoRoles(),
        ]);
        if (!alive) return;
        setBranches(branchesData);
        setAreas(areasData);
        setRoles(rolesData);
      } catch (err) {
        if (!alive) return;
        setBranches([]);
        setAreas([]);
        setRoles([]);
        window.alert(mapApiError(err).message);
      } finally {
        if (alive) setLoadingReferences(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!isEditing || !contentId) {
      const nextForm = buildFormState(null);
      const nextScope = buildScopeState(null);

      setContent(null);
      setForm(nextForm);
      setScopeAll(nextScope.all);
      setScopeBranch(nextScope.branch);
      setScopeArea(nextScope.area);
      setScopeRole(nextScope.role);
      setSelectedBranchIds(nextScope.branchIds);
      setSelectedAreaIds(nextScope.areaIds);
      setSelectedRoleIds(nextScope.roleIds);
      setInitialSnapshot(
        buildSnapshot({
          form: nextForm,
          scopeAll: nextScope.all,
          scopeBranch: nextScope.branch,
          scopeArea: nextScope.area,
          scopeRole: nextScope.role,
          branchIds: nextScope.branchIds,
          areaIds: nextScope.areaIds,
          roleIds: nextScope.roleIds,
        })
      );
      setLoadingContent(false);
      return;
    }

    let alive = true;
    (async () => {
      setLoadingContent(true);
      try {
        const nextContent = await getContenido(contentId);
        if (!alive) return;

        const nextForm = buildFormState(nextContent);
        const nextScope = buildScopeState(nextContent);

        setContent(nextContent);
        setForm(nextForm);
        setScopeAll(nextScope.all);
        setScopeBranch(nextScope.branch);
        setScopeArea(nextScope.area);
        setScopeRole(nextScope.role);
        setSelectedBranchIds(nextScope.branchIds);
        setSelectedAreaIds(nextScope.areaIds);
        setSelectedRoleIds(nextScope.roleIds);
        setInitialSnapshot(
          buildSnapshot({
            form: nextForm,
            scopeAll: nextScope.all,
            scopeBranch: nextScope.branch,
            scopeArea: nextScope.area,
            scopeRole: nextScope.role,
            branchIds: nextScope.branchIds,
            areaIds: nextScope.areaIds,
            roleIds: nextScope.roleIds,
          })
        );
      } catch (err) {
        if (!alive) return;
        window.alert(mapApiError(err).message);
        navigate("/rrhh/contenido");
      } finally {
        if (alive) setLoadingContent(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [contentId, isEditing, navigate]);

  const currentSnapshot = useMemo(
    () =>
      buildSnapshot({
        form,
        scopeAll,
        scopeBranch,
        scopeArea,
        scopeRole,
        branchIds: selectedBranchIds,
        areaIds: selectedAreaIds,
        roleIds: selectedRoleIds,
      }),
    [form, scopeAll, scopeBranch, scopeArea, scopeRole, selectedBranchIds, selectedAreaIds, selectedRoleIds]
  );

  const isDirty = useMemo(
    () => JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot),
    [currentSnapshot, initialSnapshot]
  );

  const authorLabel = content?.author?.displayName || readCurrentUserDisplayName() || "-";

  const handleToggleAll = (event) => {
    const checked = event.target.checked;
    setScopeAll(checked);
    if (checked) {
      setScopeBranch(false);
      setScopeArea(false);
      setScopeRole(false);
      setSelectedBranchIds([]);
      setSelectedAreaIds([]);
      setSelectedRoleIds([]);
    }
  };

  const handleToggleBranch = (event) => {
    const checked = event.target.checked;
    setScopeBranch(checked);
    if (checked) setScopeAll(false);
    if (!checked) setSelectedBranchIds([]);
  };

  const handleToggleArea = (event) => {
    const checked = event.target.checked;
    setScopeArea(checked);
    if (checked) setScopeAll(false);
    if (!checked) setSelectedAreaIds([]);
  };

  const handleToggleRole = (event) => {
    const checked = event.target.checked;
    setScopeRole(checked);
    if (checked) setScopeAll(false);
    if (!checked) setSelectedRoleIds([]);
  };

  const handleSave = async () => {
    const title = String(form.title || "").trim();
    if (!title) {
      window.alert("El titulo es obligatorio.");
      return;
    }

    if (!String(form.body || "").trim()) {
      window.alert("El contenido es obligatorio.");
      return;
    }

    if (!scopeAll && scopeBranch && selectedBranchIds.length === 0) {
      window.alert("Selecciona al menos una sucursal.");
      return;
    }

    if (!scopeAll && scopeArea && selectedAreaIds.length === 0) {
      window.alert("Selecciona al menos un area.");
      return;
    }

    if (!scopeAll && scopeRole && selectedRoleIds.length === 0) {
      window.alert("Selecciona al menos un rol.");
      return;
    }

    if (form.readRequirement === "WEEKLY" || form.readRequirement === "MONTHLY") {
      const repeatEvery = Number(form.readRepeatEvery);

      if (!Number.isInteger(repeatEvery) || repeatEvery < 1 || repeatEvery > 24) {
        window.alert("Indicá cada cuántas semanas/meses se debe repetir. Debe ser entre 1 y 24.");
        return;
      }
    }

    if (form.readRequirement === "WEEKLY") {
      const weekday = Number(form.readWeekday);

      if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) {
        window.alert("Seleccioná un día de la semana válido.");
        return;
      }
    }

    if (form.readRequirement === "MONTHLY") {
      const monthDay = Number(form.readMonthDay);

      if (!Number.isInteger(monthDay) || monthDay < 1 || monthDay > 31) {
        window.alert("Indicá un día del mes válido entre 1 y 31.");
        return;
      }
    }

    if (form.readUntil && !buildReadUntilIso(form.readUntil)) {
      window.alert("La fecha de finalización de repetición no es válida.");
      return;
    }

    const isWeekly = form.readRequirement === "WEEKLY";
    const isMonthly = form.readRequirement === "MONTHLY";

    const payload = {
      title,
      category: form.category,
      status: form.status,
      body: form.body,
      branchIds: scopeAll || !scopeBranch ? null : selectedBranchIds.map(Number),
      areaIds: scopeAll || !scopeArea ? null : selectedAreaIds.map(Number),
      roleIds: scopeAll || !scopeRole ? null : selectedRoleIds.map(Number),
    
      readRequirement: form.readRequirement,
      readRepeatEvery: isWeekly || isMonthly ? Number(form.readRepeatEvery) : null,
      readWeekday: isWeekly ? Number(form.readWeekday) : null,
      readMonthDay: isMonthly ? Number(form.readMonthDay) : null,
      readUntil: form.readRequirement === "NONE" ? null : buildReadUntilIso(form.readUntil),
    };

    setSaving(true);
    try {
      if (isEditing && contentId) {
        await updateContenido(contentId, payload);
      } else {
        await createContenido(payload);
      }
      navigate("/rrhh/contenido");
    } catch (err) {
      window.alert(mapApiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {/* Header */}
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <IconButton
          onClick={() => navigate("/rrhh/contenido")}
          sx={{ width: 36, height: 36, bgcolor: "secondary.500", "&:hover": { bgcolor: "secondary.600" } }}
        >
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>
        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
              {isEditing ? "Editar contenido" : "Crear contenido"}
            </Typography>
          </Pill>
        </Stack>
      </Stack>

      <Box sx={{ bgcolor: "white", borderRadius: "24px", p: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Stack spacing={2} sx={{ width: { xs: "100%", md: "40%" }, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.900" }}>
              Informacion General
            </Typography>

            <TextField
              label="Titulo"
              value={form.title}
              onChange={handleChange("title")}
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
            />

            <TextField
              select
              label="Categoria"
              value={form.category}
              onChange={handleChange("category")}
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Estado"
              value={form.status}
              onChange={handleChange("status")}
              fullWidth
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {STATUS_LABELS[option] || option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Autor"
              value={authorLabel}
              fullWidth
              size="small"
              disabled
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
            />

            <Stack spacing={1}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                Alcance
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={scopeAll} onChange={handleToggleAll} />}
                  label="Aplicar a toda la empresa"
                />
                <FormControlLabel
                  control={<Checkbox checked={scopeBranch} onChange={handleToggleBranch} />}
                  label="Sucursal"
                />
                {scopeBranch && (
                  <TextField
                    select
                    size="small"
                    value={selectedBranchIds}
                    onChange={(event) =>
                      setSelectedBranchIds(
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : String(event.target.value).split(",").filter(Boolean)
                      )
                    }
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) =>
                        renderSelectedNames(selected, branches, "Selecciona sucursales"),
                    }}
                    sx={{ ml: 4, maxWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                  >
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={String(branch.id)}>
                        <Checkbox checked={selectedBranchIds.includes(String(branch.id))} />
                        {branch.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <FormControlLabel
                  control={<Checkbox checked={scopeArea} onChange={handleToggleArea} />}
                  label="Area"
                />
                {scopeArea && (
                  <TextField
                    select
                    size="small"
                    value={selectedAreaIds}
                    onChange={(event) =>
                      setSelectedAreaIds(
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : String(event.target.value).split(",").filter(Boolean)
                      )
                    }
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) =>
                        renderSelectedNames(selected, areas, "Selecciona areas"),
                    }}
                    sx={{ ml: 4, maxWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                  >
                    {areas.map((area) => (
                      <MenuItem key={area.id} value={String(area.id)}>
                        <Checkbox checked={selectedAreaIds.includes(String(area.id))} />
                        {area.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <FormControlLabel
                  control={<Checkbox checked={scopeRole} onChange={handleToggleRole} />}
                  label="Rol"
                />
                {scopeRole && (
                  <TextField
                    select
                    size="small"
                    value={selectedRoleIds}
                    onChange={(event) =>
                      setSelectedRoleIds(
                        Array.isArray(event.target.value)
                          ? event.target.value
                          : String(event.target.value).split(",").filter(Boolean)
                      )
                    }
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) =>
                        renderSelectedNames(selected, roles, "Selecciona roles"),
                    }}
                    sx={{ ml: 4, maxWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={String(role.id)}>
                        <Checkbox checked={selectedRoleIds.includes(String(role.id))} />
                        {role.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </FormGroup>
            </Stack>

            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "primary.900" }}>
                Obligación de lectura
              </Typography>

              <TextField
                select
                label="Frecuencia"
                value={form.readRequirement}
                onChange={handleChange("readRequirement")}
                fullWidth
                size="small"
                helperText="Define si los empleados alcanzados deben leer este contenido."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
              >
                {READ_REQUIREMENT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {(form.readRequirement === "WEEKLY" || form.readRequirement === "MONTHLY") && (
                <TextField
                  label={form.readRequirement === "WEEKLY" ? "Repetir cada cuántas semanas" : "Repetir cada cuántos meses"}
                  type="number"
                  value={form.readRepeatEvery}
                  onChange={handleChange("readRepeatEvery")}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1, max: 24 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                />
              )}

              {form.readRequirement === "WEEKLY" && (
                <TextField
                  select
                  label="Día de lectura"
                  value={form.readWeekday}
                  onChange={handleChange("readWeekday")}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                >
                  {WEEKDAY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {form.readRequirement === "MONTHLY" && (
                <TextField
                  label="Día del mes"
                  type="number"
                  value={form.readMonthDay}
                  onChange={handleChange("readMonthDay")}
                  fullWidth
                  size="small"
                  inputProps={{ min: 1, max: 31 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                />
              )}

              {form.readRequirement !== "NONE" && (
                <TextField
                  label="Exigir lectura hasta"
                  type="date"
                  value={form.readUntil}
                  onChange={handleChange("readUntil")}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText="Opcional. Si queda vacío, la obligación no tiene fecha límite."
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "16px" } }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-start", mt: 2 }}>
              <Button
                onClick={() => navigate("/rrhh/contenido")}
                sx={{ textTransform: "none", fontWeight: 700, px: 3, borderRadius: "20px" }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={loadingContent || loadingReferences || !isDirty || saving}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  px: 3,
                  borderRadius: "20px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  "&:hover": { bgcolor: "primary.600" },
                  "&.Mui-disabled": { bgcolor: "neutral.200", color: "neutral.600" },
                }}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </Stack>

          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, width: { xs: "100%", md: "60%" } }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.900" }}>
              Contenido Principal
            </Typography>
            <RichTextEditor
              value={form.body}
              onChange={(html) => setForm((prev) => ({ ...prev, body: html }))}
              placeholder="Escribe o pega tu contenido aquí..."
              minHeight={400}
              readOnly={false}
            />
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
